import React from 'react';
import { 
  Users, 
  Target,
  Calendar,
  Award,
  Zap,
  BarChart3,
  MessageSquare,
  Eye
} from 'lucide-react';

const Sprint10Report = () => {
  return (
    <div className="status-report">
      <div className="report-header">
        <h1 className="report-title">
          <Calendar className="w-8 h-8 text-blue-600" />
          Leadership Team Update - Sprint 10 & Baldwin Pilot
        </h1>
        <div className="report-meta">
          <span className="meta-item">
            <Calendar className="w-4 h-4" />
            September 2025
          </span>
          <span className="meta-item">
            <Target className="w-4 h-4" />
            Sprint 10 Progress
          </span>
          <span className="meta-item">
            <Users className="w-4 h-4" />
            Baldwin Pilot Active
          </span>
        </div>
      </div>

      <div className="report-grid">
        {/* Sprint 10 Key Highlights */}
        <div className="report-section">
          <h2 className="section-title">
            <Zap className="w-6 h-6 text-yellow-600" />
            Sprint 10 Key Highlights
          </h2>
          
          <div className="achievement-cards">
            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Kinetic Enhancement</h3>
                <span className="status-badge completed">IN DEVELOPMENT</span>
              </div>
              <ul className="achievement-list">
                <li>Launched development of "adding project" feature</li>
                <li>Excel data extraction capability now operational</li>
                <li>Streamlined project setup workflows</li>
                <li>Enhanced data processing capabilities</li>
              </ul>
            </div>

            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Quality Improvements</h3>
                <span className="status-badge completed">COMPLETED</span>
              </div>
              <ul className="achievement-list">
                <li><span className="metric">11 bugs</span> resolved in Asuretify Monorepo</li>
                <li>Improved system stability and user experience</li>
                <li>Critical production issues addressed</li>
              </ul>
            </div>

            <div className="achievement-card">
              <div className="achievement-header">
                <h3>Document Processing</h3>
                <span className="status-badge completed">COMPLETED</span>
              </div>
              <ul className="achievement-list">
                <li><span className="metric">110</span> Certificate of Insurance (COI) documents</li>
                <li><span className="metric">100</span> Policy documents</li>
                <li>Ground truth creation completed</li>
                <li>Currently under review for accuracy</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Baldwin Pilot Update */}
        <div className="report-section">
          <h2 className="section-title">
            <Users className="w-6 h-6 text-green-600" />
            Baldwin Pilot Update
          </h2>
          
          <div className="initiative-cards">
            <div className="initiative-card">
              <h3>Training Session Success</h3>
              <div className="metrics-grid">
                <div className="metric">
                  <span className="metric-value">✓</span>
                  <span className="metric-label">Training Complete</span>
                </div>
                <div className="metric">
                  <span className="metric-value">High</span>
                  <span className="metric-label">Engagement</span>
                </div>
              </div>
              <ul>
                <li>Matt conducted comprehensive training on Thursday</li>
                <li>Strong attendee engagement and active participation</li>
                <li>Identified several technically proficient individuals</li>
                <li>Technical expertise valuable for detailed feedback</li>
              </ul>
            </div>

            <div className="initiative-card">
              <h3>Support Infrastructure</h3>
              <ul>
                <li>Dedicated task team established</li>
                <li>Real-time communication channel active</li>
                <li>Clear support protocols defined</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Feedback Collection Strategy */}
        <div className="report-section">
          <h2 className="section-title">
            <MessageSquare className="w-6 h-6 text-purple-600" />
            Feedback Collection Strategy
          </h2>
          
          <div className="poc-grid">
            <div className="poc-section">
              <h3>Structured Approach</h3>
              <div className="poc-cards">
                <div className="poc-card">
                  <h4>Baldwin Feedback</h4>
                  <p>Spreadsheet feedback due by <span className="metric">Tuesday</span></p>
                  <span className="status-badge pending">Pending</span>
                </div>
                <div className="poc-card">
                  <h4>In-App Feedback</h4>
                  <p>Developing for RG platform</p>
                  <span className="status-badge development">In Development</span>
                </div>
              </div>
            </div>

            <div className="poc-section">
              <h3>Analytics Implementation</h3>
              <div className="tech-cards">
                <div className="tech-card">
                  <h4>PostHog Integration</h4>
                  <p>Primary analytics platform for real-time insights</p>
                </div>
              </div>
              <p className="tech-note">Evaluating PostHog for user behavior and system performance analytics</p>
            </div>
          </div>
        </div>

        {/* Proactive Monitoring */}
        <div className="report-section">
          <h2 className="section-title">
            <Eye className="w-6 h-6 text-blue-600" />
            Proactive Monitoring Approach
          </h2>
          
          <div className="timeline-cards">
            <div className="timeline-card">
              <h3>Daily Cycle</h3>
              <ul>
                <li>Review analytics daily to catch issues early</li>
                <li>Identify potential problems before user impact</li>
              </ul>
            </div>
            <div className="timeline-card">
              <h3>Tuesday</h3>
              <ul>
                <li>Receive Baldwin feedback report</li>
                <li>Confirm our analytics findings</li>
              </ul>
            </div>
            <div className="timeline-card">
              <h3>Thursday</h3>
              <ul>
                <li>Present actionable fix plans, OR</li>
                <li>Prepare specific questions for deeper insights</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Next Steps */}
        <div className="report-section">
          <h2 className="section-title">
            <Target className="w-6 h-6 text-orange-600" />
            Next Steps
          </h2>
          
          <div className="roadmap-cards">
            <div className="roadmap-card">
              <h3>Baldwin Pilot</h3>
              <ul>
                <li>Continue monitoring pilot progress</li>
                <li>Gather and analyze initial feedback</li>
                <li>Implement feedback-driven improvements</li>
              </ul>
            </div>
            <div className="roadmap-card">
              <h3>Development</h3>
              <ul>
                <li>Complete review of ground truth documents</li>
                <li>Further develop Kinetic project addition feature</li>
                <li>Implement PostHog analytics platform</li>
              </ul>
            </div>
            <div className="roadmap-card">
              <h3>Process Improvement</h3>
              <ul>
                <li>Refine feedback collection mechanisms</li>
                <li>Optimize monitoring and analytics workflows</li>
                <li>Scale successful pilot practices</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="report-section">
          <h2 className="section-title">
            <BarChart3 className="w-6 h-6 text-indigo-600" />
            Key Metrics
          </h2>
          
          <div className="metrics-table">
            <table>
              <thead>
                <tr>
                  <th>Metric</th>
                  <th>Current Status</th>
                  <th>Target</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Bugs Resolved</td>
                  <td>11 in Asuretify</td>
                  <td>✓ Exceeded</td>
                </tr>
                <tr>
                  <td>Documents Processed</td>
                  <td>210 total</td>
                  <td>Ground Truth Complete</td>
                </tr>
                <tr>
                  <td>COI Documents</td>
                  <td>110 processed</td>
                  <td>Under Review</td>
                </tr>
                <tr>
                  <td>Policy Documents</td>
                  <td>100 processed</td>
                  <td>Under Review</td>
                </tr>
                <tr>
                  <td>Baldwin Training</td>
                  <td>Completed</td>
                  <td>✓ Successful</td>
                </tr>
                <tr>
                  <td>Feedback Collection</td>
                  <td>Tuesday Deadline</td>
                  <td>In Progress</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Success Indicators */}
        <div className="report-section">
          <h2 className="section-title">
            <Award className="w-6 h-6 text-yellow-600" />
            Success Indicators
          </h2>
          
          <div className="recognition-cards">
            <div className="recognition-card">
              <h3>Technical Excellence</h3>
              <p>Identified technically proficient individuals in Baldwin team</p>
            </div>
            <div className="recognition-card">
              <h3>Process Innovation</h3>
              <p>Implemented proactive monitoring approach</p>
            </div>
            <div className="recognition-card">
              <h3>Quality Focus</h3>
              <p>Ground truth creation completed with 210 documents</p>
            </div>
          </div>
        </div>
      </div>

      <div className="report-footer">
        <p><strong>Prepared by:</strong> Naqi & Claude</p>
        <p><strong>Next Review:</strong> Baldwin Feedback Analysis (Tuesday)</p>
        <p><strong>Status:</strong> Sprint 10 Active, Baldwin Pilot Ongoing</p>
      </div>
    </div>
  );
};

export default Sprint10Report;
